import axios, { HttpStatusCode } from "axios";
import { Model } from "../models/model";

export class BaseApi {
  #api;
  #baseURL;
  #httpClient;
  #model;
  #config;
  #root;

  constructor({ api, baseURL, model, httpClient, config, root }) {
    this.#api = api;
    this.#baseURL = baseURL ?? "";
    this.#httpClient = httpClient ?? axios;
    this.#model = model ?? Model;
    this.#config = config ?? {};
    this.#root = root;
    this.#enforceAcceptHeader();
  }

  #enforceAcceptHeader() {
    if (!this.#httpClient.defaults?.headers?.common?.Accept) {
      return;
    }

    this.#httpClient.defaults.headers.common.Accept = "application/json";
  }

  parse(data) {
    return new this.#model(data);
  }

  transform(data) {
    if (this.#root) {
      data = data?.[this.#root];
    }
    if (!data) {
      return null;
    }
    if (Array.isArray(data)) {
      return data.map((datum) => this.parse(datum));
    }
    return this.parse(data);
  }

  async get(path, query = {}) {
    const params = this.params;

    for (const [key, value] of Object.entries(query)) {
      params.append(key, value);
    }

    return this.request("get", path, { params });
  }

  async post(path, data) {
    return this.request("post", path, { data });
  }

  async put(path, data) {
    return this.request("put", path, { data });
  }

  async patch(path, data = {}) {
    return this.request("patch", path, { data });
  }

  async delete(path) {
    return this.request("delete", path);
  }

  async request(method, url, options = {}) {
    const response = await this.#httpClient.request({
      method,
      url,
      params: this.params,
      ...this.config,
      ...options,
    });

    if (response.status === HttpStatusCode.NoContent) {
      return null;
    }

    return this.transform(response.data);
  }

  get params() {
    const params = new URLSearchParams();
    if (this.#api) {
      params.append("api", this.#api);
    }

    return params;
  }

  get config() {
    const isFullyQualifiedURL = this.#baseURL?.startsWith("http");

    return {
      ...this.#config,
      baseURL:
        !isFullyQualifiedURL && axios.defaults.embedHost
          ? `${axios.defaults.embedHost}${this.#baseURL}`
          : this.#baseURL,
    };
  }
}
